import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useEffect, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import {
  AppBuildState,
  InternScreensAITextGeneratorQuery,
} from "./__generated__/InternScreensAITextGeneratorQuery.graphql";
import CardSection from "../../../components/CardSection";
import useBrandGenerateScreensContentAsyncMutation, {
  ScreensGeneratedContentWithImages,
} from "../../../mutations/useBrandGenerateScreensAsyncMutation";
import useBrandUpdateGeneratedContentMutation from "../../../mutations/useBrandUpdateGeneratedContentMutation";
import nullthrows from "../../../utils/nullthrows";
import InternTempoConnectionCard from "./InternTempoConnectionCard";

const GeneratedImageDisplay = ({
  images,
  title,
}: {
  images: ReadonlyArray<string> | null;
  title: string;
}) => {
  if (!images || images.length === 0) {
    return (
      <Box>
        <Typography variant="body0" fontWeight="bold">
          {title}:
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: 2, mt: 1 }}
        >
          No images available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body0" fontWeight="bold">
        {title}:
      </Typography>
      <Stack spacing={1} sx={{ ml: 2, mt: 1 }}>
        {images.map((imageData: string, index: number) => (
          <img
            key={`${title}-${index}`}
            src={`data:image/png;base64,${imageData}`}
            alt={`Generated ${title.toLowerCase()} ${index + 1}`}
            style={{
              width: "200px",
              height: "200px",
              objectFit: "contain",
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
            loading="lazy"
          />
        ))}
      </Stack>
    </Box>
  );
};

const query = graphql`
  query InternScreensAITextGeneratorQuery($id: ID!) {
    brand(id: $id) {
      id
      ...InternTempoConnectionCard_brand
      appConfig {
        appDescription
        appState
      }
    }
  }
`;

// Extract individual content types from GeneratedAllText
type ContentSection =
  NonNullable<ScreensGeneratedContentWithImages>[keyof NonNullable<ScreensGeneratedContentWithImages>];

const InternScreensAITextGenerator = ({ brandID }: { brandID: string }) => {
  const [generatedText, setGeneratedText] =
    useState<ScreensGeneratedContentWithImages | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);

  const [brandDescription, setBrandDescription] = useState("");
  const [brandState, setBrandState] = useState<AppBuildState>();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const data = useLazyLoadQuery<InternScreensAITextGeneratorQuery>(query, {
    id: nullthrows(brandID),
  });

  useEffect(() => {
    if (data.brand?.appConfig?.appDescription) {
      setBrandDescription(data.brand.appConfig.appDescription);
    }
    if (data.brand?.appConfig?.appState) {
      setBrandState(data.brand.appConfig.appState);
    }
  }, [data.brand?.appConfig]);

  const { generateScreensContentAsync, isGenerating, generatedData } =
    useBrandGenerateScreensContentAsyncMutation();

  useEffect(() => {
    if (generatedData) {
      setGeneratedText(generatedData);
    }
  }, [generatedData]);

  const [saveGeneratedContent, isSaving] =
    useBrandUpdateGeneratedContentMutation();

  const onSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    setError(null);
    setGeneratedText(null);
    generateScreensContentAsync({
      brandId: brandID,
      brandDescription: brandDescription || null,
    });
  };

  const onSave = () => {
    setIsConfirmDialogOpen(true);
  };

  const onConfirmDialogClose = () => {
    setIsConfirmDialogOpen(false);
  };

  const onConfirmSave = () => {
    if (!generatedText) return;

    const saveData = {
      notificationOptIn: generatedText.notificationOptIn
        ? {
            title: generatedText.notificationOptIn.title,
            subtitle: generatedText.notificationOptIn.subtitle,
            backgroundImage: generatedText.notificationOptIn.backgroundImage,
          }
        : null,
      onboarding: generatedText.onboarding
        ? {
            rootTitle: generatedText.onboarding.rootTitle,
            upsellTitle: generatedText.onboarding.upsellTitle,
            upsellSubtitle: generatedText.onboarding.upsellSubtitle,
            backgroundImage: generatedText.onboarding.backgroundImage,
          }
        : null,
      account: generatedText.account
        ? {
            deletionDialogTitle: generatedText.account.deletionDialogTitle,
            deletionSubtitle: generatedText.account.deletionSubtitle,
            registerTitle: generatedText.account.registerTitle,
            registerSubtitle: generatedText.account.registerSubtitle,
            signInTitle: generatedText.account.signInTitle,
            signInSubtitle: generatedText.account.signInSubtitle,
          }
        : null,
      automation: generatedText.automation
        ? {
            abandonedCartNotifications:
              generatedText.automation.abandonedCartNotifications,
            abandonedBrowseNotifications:
              generatedText.automation.abandonedBrowseNotifications,
            abandonedCheckoutNotifications:
              generatedText.automation.abandonedCheckoutNotifications,
            welcomeSeriesNotifications:
              generatedText.automation.welcomeSeriesNotifications,
            reengagementNotifications:
              generatedText.automation.reengagementNotifications,
          }
        : null,
      appListing: generatedText.appListing
        ? {
            appSubtitle: generatedText.appListing.appSubtitle,
            promotionalText: generatedText.appListing.promotionalText,
            appDescription: generatedText.appListing.appDescription,
            keywords: generatedText.appListing.keywords,
            appClipSubtitle: generatedText.appListing.appClipSubtitle,
            appClipButtonText: generatedText.appListing.appClipButtonText,
            primaryCategory: generatedText.appListing.primaryCategory,
            secondaryCategory: generatedText.appListing.secondaryCategory,
            appClipHeader: generatedText.appListing.appClipHeader,
            appIcon: generatedText.appListing.appIcon,
          }
        : null,
    };

    saveGeneratedContent(
      brandID,
      saveData,
      () => {
        onConfirmDialogClose();
      },
      (errors) => {
        setError(errors[0]?.message || "Failed to save content");
        onConfirmDialogClose();
      }
    );
  };

  const formContent = (
    <Stack spacing={3}>
      <TextField
        value={brandDescription}
        onChange={(e) => setBrandDescription(e.target.value)}
        label="Brand Description (Optional)"
        placeholder="Enter custom brand description for testing"
        fullWidth
        multiline
        rows={3}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
      />

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={isGenerating}
          startIcon={isGenerating ? <CircularProgress size={20} /> : null}
        >
          {isGenerating ? "Generating..." : "Generate"}
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          color="secondary"
          disabled={isSaving || !generatedText || brandState === "PRODUCTION"}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Stack>
  );

  const renderContentSection = (content: ContentSection) => {
    if (!content) return <></>;

    const renderValue = (value: NonNullable<ContentSection>, key: string) => {
      // Handle image arrays (base64 encoded images)
      if (
        Array.isArray(value) &&
        (key.includes("Image") ||
          key.includes("Header") ||
          key.includes("Icon"))
      ) {
        const imageArray = value as ReadonlyArray<string>;
        const title = key
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());

        return (
          <GeneratedImageDisplay key={key} images={imageArray} title={title} />
        );
      }

      if (Array.isArray(value)) {
        return (
          <Box key={key}>
            <Typography variant="body0" fontWeight="bold">
              {key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())}
              :
            </Typography>
            <Stack spacing={1} sx={{ ml: 2 }}>
              {value.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    padding: 1,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                  }}
                >
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    Notification {index + 1}:
                  </Typography>
                  <Stack spacing={0.5} sx={{ ml: 1 }}>
                    {Object.entries(item).map(([itemKey, itemValue]) => (
                      <Box key={itemKey}>
                        <Typography variant="body2" fontWeight="medium">
                          {itemKey
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())}
                          :
                        </Typography>
                        <Typography variant="body2">
                          {String(itemValue)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        );
      }

      return (
        <Box key={key}>
          <Typography variant="body0" fontWeight="bold">
            {key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase())}
            :
          </Typography>
          <Typography>{String(value)}</Typography>
        </Box>
      );
    };

    return (
      <Box
        sx={{
          padding: 2,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={1}>
          {Object.entries(content).map(([key, value]) =>
            renderValue(value as NonNullable<ContentSection>, key)
          )}
        </Stack>
      </Box>
    );
  };

  const renderGeneratedContent = () => {
    if (!generatedText) return <></>;

    const contentTypes = [
      {
        key: "notificationOptIn",
        title: "Notification Opt-in",
      },
      { key: "onboarding", title: "Onboarding" },
      { key: "account", title: "Account" },
      { key: "automation", title: "Automation" },
      { key: "appListing", title: "App Listing" },
    ];

    const availableContentTypes = contentTypes.filter(
      ({ key }) => generatedText[key as keyof ScreensGeneratedContentWithImages]
    );

    return (
      <Stack spacing={2}>
        {availableContentTypes.length > 0 && (
          <>
            <Tabs
              value={selectedTab}
              onChange={(_, newValue) => setSelectedTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {availableContentTypes.map(({ title }, index) => (
                <Tab key={title} label={title} />
              ))}
            </Tabs>

            <Box sx={{ mt: 2 }}>
              {availableContentTypes.map(({ key, title }, index) => {
                const content =
                  generatedText[key as keyof ScreensGeneratedContentWithImages];
                return (
                  <Box
                    key={title}
                    sx={{ display: selectedTab === index ? "block" : "none" }}
                  >
                    {renderContentSection(content)}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Stack>
    );
  };

  return (
    <Stack spacing={3} paddingBottom={5}>
      <InternTempoConnectionCard brand={data.brand} />
      <CardSection title="AI Content Generator" content={formContent} />

      {error && (
        <Alert severity="error">
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      {generatedText && (
        <CardSection
          title="Generated Content"
          content={renderGeneratedContent()}
        />
      )}

      <Dialog open={isConfirmDialogOpen} onClose={onConfirmDialogClose}>
        <DialogTitle>
          Are you sure you want to save the generated content?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will update your brand's content with the AI-generated text and
            images. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onConfirmDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            disabled={isSaving}
            onClick={onConfirmSave}
            color="secondary"
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default InternScreensAITextGenerator;
